import type { LiveActivityPageTabs } from '@/app/(authenticated)/(dashboard)/live-activity/constants';
import type { SettingPageTabs } from '@/app/(authenticated)/(dashboard)/settings/constants';
import type { AdminPanelSection } from '@/domains/admin-panel/admin-panel-sidebar.config';
import type { RoiSettingsTab } from '@/domains/reports/report-page/roi/roi-settings-tabs';
import type { NebulyPageRoute } from '@/domains/pages/types';
import type { LinkTarget } from '@/types/navigation';

/** Routes that sync the legacy `homeTab` query param during app-router migration. */
export type HomeTab = '/reports' | '/all-reports' | '/live-activity' | '/settings';

export type AppRouteParams = Record<string, string | undefined>;

export type AppNavigationOptions = {
  params?: AppRouteParams;
  keepList?: string[];
  target?: LinkTarget;
  /** Full page reload instead of Next.js client navigation. */
  nativeRouter?: boolean;
  /** Overrides the resolved pathname when opening in a new tab. */
  pathname?: string;
  /** Runs immediately before navigation (e.g. set filter state). */
  before?: () => void;
};

export type NavigateAppRequest =
  | (AppNavigationOptions & { route: '/reports' })
  | (AppNavigationOptions & { route: '/all-reports' })
  | (AppNavigationOptions & { route: '/live-activity' })
  | (AppNavigationOptions & { route: '/settings' })
  | (AppNavigationOptions & { route: '/explore' })
  | (AppNavigationOptions & { route: '/taxonomy' })
  | (AppNavigationOptions & { route: 'live-activity-section'; section: LiveActivityPageTabs })
  | (AppNavigationOptions & { route: 'settings-section'; section: SettingPageTabs })
  | (AppNavigationOptions & { route: 'roi-setup-section'; section: RoiSettingsTab })
  | (AppNavigationOptions & { route: 'admin-panel-section'; section: AdminPanelSection })
  | (AppNavigationOptions & { route: NebulyPageRoute });

export const HOME_TAB_ROUTES = ['/reports', '/all-reports', '/live-activity', '/settings'] as const satisfies readonly HomeTab[];

export function isHomeTabRoute(pathname: string): pathname is HomeTab {
  return (HOME_TAB_ROUTES as readonly string[]).includes(pathname);
}

export function resolveHomeTab(pathname: string): HomeTab | null {
  if (pathname === '/live-activity' || pathname.startsWith('/live-activity/')) {
    return '/live-activity';
  }

  if (pathname === '/settings' || pathname.startsWith('/settings/')) {
    return '/settings';
  }

  return isHomeTabRoute(pathname) ? pathname : null;
}
